import { faMicrophoneSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import { NumberPad } from '@/components/NumberPad/NumberPad';
import { isSoftPhoneActive, useSoftPhone } from '@/contexts/softPhoneContext';
import useMicPermission from '@/hooks/useMicPermission';

interface Props {
  onClose: () => void;
  className?: string;
}

const normalizeForDial = (raw: string): string => {
  const stripped = raw.replace(/[^\d+*#]/g, '');
  if (stripped.startsWith('+')) return stripped;
  if (stripped.length === 10) return `+1${stripped}`;
  if (stripped.length === 11 && stripped.startsWith('1')) return `+${stripped}`;
  return stripped;
};

export const DialpadPopover = ({ onClose, className = '' }: Props) => {
  const softPhone = useSoftPhone();
  const { micNotApproved, micMessage, openSettings, extensionSettingsUrl, siteSettingsUrl } =
    useMicPermission();
  const [error, setError] = useState<string | null>(null);

  const handleDial = async (number: string) => {
    if (!isSoftPhoneActive(softPhone)) return;
    setError(null);
    try {
      await softPhone.startCall(normalizeForDial(number));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start call');
    }
  };

  return (
    <div className={`flex flex-col items-end gap-1 ${className}`}>
      <NumberPad
        mode="dialpad"
        onDial={handleDial}
        onClose={onClose}
        callDisabled={micNotApproved}
      />
      {micNotApproved && (
        <div className="flex flex-col gap-1 bg-gray-900 rounded px-2 py-1.5 min-w-56 w-full text-xs text-yellow-400">
          <span className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faMicrophoneSlash} />
            {micMessage}
          </span>
          <span className="flex gap-2 text-gray-400">
            {extensionSettingsUrl && (
              <button
                type="button"
                className="underline hover:text-gray-300"
                onClick={() => openSettings!(extensionSettingsUrl)}>
                Extension settings
              </button>
            )}
            <button
              type="button"
              className="underline hover:text-gray-300"
              onClick={() =>
                openSettings
                  ? openSettings(siteSettingsUrl)
                  : navigator.clipboard?.writeText(siteSettingsUrl).catch(() => {})
              }>
              Site settings
            </button>
          </span>
        </div>
      )}
      {error && (
        <p className="text-red-400 text-xs bg-gray-900 rounded px-2 py-1 min-w-56 w-full">
          {error}
        </p>
      )}
    </div>
  );
};
